import React, { useState, useEffect, useMemo } from 'react';
import { InputLabel, MenuItem } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '../input/textField';
import Select from '../input/select';
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    input: {
        margin: '12px 0px !important',
    },
    selectLabel: {
        marginBottom: '4px',
        marginTop: '12px',
    },
    autocompleteOption: {
        color: '#000000 !important',
        backgroundColor: '#ffffff !important',
        '&[data-focus="true"]': {
            backgroundColor: '#f5f5f5 !important',
        },
        '&[aria-selected="true"]': {
            backgroundColor: '#e0e0e0 !important',
        },
    },
    autocompleteListbox: {
        backgroundColor: '#ffffff !important',
        '& .MuiAutocomplete-option': {
            color: '#000000 !important',
        },
    },
}));

/**
 * DynamicRecipientForm
 * Renders form fields dynamically based on configuration from BFF
 * Handles nested dropdowns with cascading field visibility
 */
const DynamicRecipientForm = ({ 
    configuration, 
    onValuesChange,
    submitAttempted = false,
    initialCountryCode = null
}) => {
    const classes = useStyles();
    const [fieldValues, setFieldValues] = useState({});
    const [selectedCountry, setSelectedCountry] = useState(null);

    // Get countries from configuration with popular ones first
    const countries = useMemo(() => {
        const items = configuration?.banks?.items || [];
        
        // Popular countries to show first
        const popularCodes = ['US', 'GB', 'CA', 'AU', 'SG', 'HK', 'JP', 'MX'];
        const popular = items.filter(c => popularCodes.includes(c.countryCode));
        const others = items.filter(c => !popularCodes.includes(c.countryCode));
        
        return [...popular, ...others];
    }, [configuration]);

    // Initialize with first country or provided initialCountryCode
    useEffect(() => {
        if (countries.length > 0 && !selectedCountry) {
            const initialCountry = initialCountryCode 
                ? countries.find(c => c.countryCode === initialCountryCode)
                : countries[0];
            if (initialCountry) {
                setSelectedCountry(initialCountry);
                updateFieldValue('countryOfBank', initialCountry.countryCode);
            }
        }
    }, [countries, initialCountryCode, selectedCountry]);

    // Notify parent of value changes
    useEffect(() => {
        if (onValuesChange) {
            onValuesChange(fieldValues, isFormValid());
        }
    }, [fieldValues]);

    const updateFieldValue = (fieldName, value) => {
        setFieldValues(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    // Get currently visible fields based on selections
    const getVisibleFields = () => {
        if (!selectedCountry) return { textFields: [], dropdownFields: [], subItems: [] };

        const fields = selectedCountry.fields || { textFields: [], dropdownFields: [] };
        const subItems = selectedCountry.subItems || [];

        return {
            textFields: fields.textFields || [],
            dropdownFields: fields.dropdownFields || [],
            subItems: subItems
        };
    };

    // Get nested fields based on dropdown selection
    const getNestedFields = (subItem) => {
        const selectedValue = fieldValues[subItem.fieldName];
        
        if (!selectedValue || !subItem.options) {
            return { textFields: [], dropdownFields: [] };
        }

        const selectedOption = subItem.options.find(opt => opt.value === selectedValue);
        
        if (!selectedOption || !selectedOption.fields) {
            return { textFields: [], dropdownFields: [] };
        }

        return {
            textFields: selectedOption.fields.textFields || [],
            dropdownFields: selectedOption.fields.dropdownFields || [],
            subItems: []
        };
    };

    // Validate form
    const isFormValid = () => {
        if (!selectedCountry) return false;

        const visibleFields = getVisibleFields();
        
        // Check top-level text fields
        for (const field of visibleFields.textFields || []) {
            if (field.isRequired && !fieldValues[field.fieldName]) {
                return false;
            }
        }

        // Check top-level dropdown fields
        for (const field of visibleFields.dropdownFields || []) {
            if (field.isRequired && !fieldValues[field.fieldName]) {
                return false;
            }
        }

        // Check subItems (nested dropdowns)
        for (const subItem of visibleFields.subItems || []) {
            if (subItem.isRequired && !fieldValues[subItem.fieldName]) {
                return false;
            }

            // Check nested fields within selected option
            const nestedFields = getNestedFields(subItem);
            for (const nestedField of nestedFields.textFields || []) {
                if (nestedField.isRequired && !fieldValues[nestedField.fieldName]) {
                    return false;
                }
            }
            for (const nestedField of nestedFields.dropdownFields || []) {
                if (nestedField.isRequired && !fieldValues[nestedField.fieldName]) {
                    return false;
                }
            }
        }

        return true;
    };

    // Render a text field
    const renderTextField = (field) => {
        const hasError = submitAttempted && field.isRequired && !fieldValues[field.fieldName];
        
        return (
            <TextField
                key={field.fieldName}
                className={classes.input}
                name={field.fieldName}
                label={field.labelText}
                placeholder={field.placeholderText}
                value={fieldValues[field.fieldName] || ''}
                onChange={(e) => updateFieldValue(field.fieldName, e.target.value)}
                variant="outlined"
                fullWidth
                required={field.isRequired}
                error={hasError}
                helperText={hasError ? `${field.labelText} is required` : ''}
                type={field.keyboardType === 'NUMBER' ? 'number' : 'text'}
                inputProps={{
                    inputMode: field.keyboardType === 'NUMBER' ? 'numeric' : 'text'
                }}
            />
        );
    };

    // Render a dropdown field
    const renderDropdownField = (field) => {
        const hasError = submitAttempted && field.isRequired && !fieldValues[field.fieldName];
        
        return (
            <div key={field.fieldName}>
                <InputLabel className={classes.selectLabel}>
                    {field.labelText}{field.isRequired ? ' *' : ''}
                </InputLabel>
                <Select
                    className={classes.input}
                    name={field.fieldName}
                    value={fieldValues[field.fieldName] || ''}
                    onChange={(e) => updateFieldValue(field.fieldName, e.target.value)}
                    variant="outlined"
                    fullWidth
                    required={field.isRequired}
                    error={hasError}
                    displayEmpty
                >
                    <MenuItem value="" disabled>
                        {field.placeholderText}
                    </MenuItem>
                    {(field.options || []).map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.displayName}
                        </MenuItem>
                    ))}
                </Select>
            </div>
        );
    };

    // Render a subItem (nested dropdown with cascading fields)
    const renderSubItem = (subItem) => {
        const hasError = submitAttempted && subItem.isRequired && !fieldValues[subItem.fieldName];
        const nestedFields = getNestedFields(subItem);
        
        return (
            <div key={subItem.fieldName}>
                <InputLabel className={classes.selectLabel}>
                    {subItem.labelText}{subItem.isRequired ? ' *' : ''}
                </InputLabel>
                <Select
                    className={classes.input}
                    name={subItem.fieldName}
                    value={fieldValues[subItem.fieldName] || ''}
                    onChange={(e) => {
                        // Clear nested field values when parent changes
                        const newValues = { ...fieldValues };
                        const oldValue = fieldValues[subItem.fieldName];
                        if (oldValue !== e.target.value) {
                            // Clear all nested fields
                            const oldOption = (subItem.options || []).find(opt => opt.value === oldValue);
                            if (oldOption && oldOption.fields) {
                                (oldOption.fields.textFields || []).forEach(f => {
                                    delete newValues[f.fieldName];
                                });
                                (oldOption.fields.dropdownFields || []).forEach(f => {
                                    delete newValues[f.fieldName];
                                });
                            }
                        }
                        newValues[subItem.fieldName] = e.target.value;
                        setFieldValues(newValues);
                    }}
                    variant="outlined"
                    fullWidth
                    required={subItem.isRequired}
                    error={hasError}
                    displayEmpty
                >
                    <MenuItem value="" disabled>
                        {subItem.placeholderText}
                    </MenuItem>
                    {(subItem.options || []).map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.displayName}
                        </MenuItem>
                    ))}
                </Select>

                {/* Render nested fields based on selection */}
                {fieldValues[subItem.fieldName] && (
                    <>
                        {nestedFields.textFields.map(renderTextField)}
                        {nestedFields.dropdownFields.map(renderDropdownField)}
                    </>
                )}
            </div>
        );
    };

    const visibleFields = getVisibleFields();

    return (
        <div className={classes.inputContainer}>
            {/* Country of bank Selector with Autocomplete */}
            <InputLabel className={classes.selectLabel}>Country of bank *</InputLabel>
            <Autocomplete
                className={classes.input}
                options={countries}
                value={selectedCountry}
                onChange={(event, newValue) => {
                    setSelectedCountry(newValue);
                    // Clear all field values when country changes
                    setFieldValues({ countryOfBank: newValue?.countryCode || '' });
                }}
                getOptionLabel={(option) => option.longName || ''}
                getOptionSelected={(option, value) => option.countryCode === value?.countryCode}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Type to search countries..."
                        variant="outlined"
                        required
                    />
                )}
                disableClearable
                classes={{
                    option: classes.autocompleteOption,
                    listbox: classes.autocompleteListbox,
                }}
                ListboxProps={{
                    style: {
                        backgroundColor: '#ffffff',
                        color: '#000000',
                    }
                }}
            />

            {/* Top-level dropdown fields */}
            {visibleFields.dropdownFields.map(renderDropdownField)}

            {/* Top-level text fields */}
            {visibleFields.textFields.map(renderTextField)}

            {/* SubItems (currency, transferType, recipientType, etc.) */}
            {visibleFields.subItems.map(renderSubItem)}
        </div>
    );
};

export default DynamicRecipientForm;

